import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { ClientPortalApiService } from '../../services/client-portal-api.service';
import { AuthStateService } from '@hypenation/auth';
import {
  CustomerDashboardDto,
  FavouriteOrganiser,
} from '../../models/client-portal.models';
import {
  EventModel,
  EventsListComponent,
  ApiService,
  PaginationResponse,
} from '@hypenation/tickets';
import { FilesService } from '@hypenation/shared';
import { HttpResponse } from '@angular/common/http';
import { environment } from 'environments/environment';

@Component({
  selector: 'hypenation-client-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzCardModule,
    NzIconModule,
    NzGridModule,
    NzStatisticModule,
    NzSpinModule,
    NzButtonModule,
    NzEmptyModule,
    NzTagModule,
    EventsListComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit, OnDestroy {
  loading = false;
  dashboardData: CustomerDashboardDto | null = null;
  favouriteOrganisers: FavouriteOrganiser[] = [];
  comingSoonEvents: EventModel[] = [];
  featuredEvents: EventModel[] = [];
  featuredEventsLoading = false;
  currentFeaturedIndex = 0;
  featuredImageUrls = new Map<number, string>();
  private featuredInterval: any;
  userEmail: string | null = null;

  private readonly apiService = inject(ClientPortalApiService);
  private readonly ticketsApiService = inject(ApiService);
  private readonly filesService = inject(FilesService);
  private readonly authStateService = inject(AuthStateService);

  get organiserSignupUrl(): string {
    return environment.organiserPortalUrl + '#/auth/signup';
  }

  get organiserLoginUrl(): string {
    return environment.organiserPortalUrl + '#/auth/login';
  }

  ngOnDestroy(): void {
    if (this.featuredInterval) clearInterval(this.featuredInterval);
  }

  ngOnInit(): void {
    this.loadFeaturedEvents();
    this.userEmail = this.authStateService.getUserEmail();
    if (this.userEmail) {
      this.loadDashboardData();
      this.loadComingSoonEvents();
      this.loadFavouriteOrganisers();
    }
  }

  loadDashboardData(): void {
    if (!this.userEmail) return;
    this.loading = true;
    this.apiService.getDashboard(this.userEmail).subscribe({
      next: (res: HttpResponse<CustomerDashboardDto>) => {
        this.dashboardData = res.body;
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  loadFeaturedEvents(): void {
    this.featuredEventsLoading = true;
    this.ticketsApiService.getFeaturedEvents(0, 10).subscribe({
      next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
        this.featuredEvents = res.body?.content ?? [];
        this.featuredEventsLoading = false;
        this.featuredEvents.forEach((event, i) => {
          if (event.imageFileName) {
            this.filesService
              .getFileDownloadUrl(event.imageFileName)
              .subscribe({
                next: (url) => this.featuredImageUrls.set(i, url),
                error: () =>
                  this.featuredImageUrls.set(i, 'assets/placeholder.jpg'),
              });
          }
        });
        if (this.featuredEvents.length > 1) {
          this.featuredInterval = setInterval(() => {
            this.currentFeaturedIndex =
              (this.currentFeaturedIndex + 1) % this.featuredEvents.length;
          }, 6000);
        }
      },
      error: () => (this.featuredEventsLoading = false),
    });
  }

  getFeaturedImageUrl(): string {
    return (
      this.featuredImageUrls.get(this.currentFeaturedIndex) ||
      'assets/placeholder.jpg'
    );
  }

  goToFeatured(index: number): void {
    this.currentFeaturedIndex = index;
    // Reset auto-rotate timer
    if (this.featuredInterval) {
      clearInterval(this.featuredInterval);
      this.featuredInterval = setInterval(() => {
        this.currentFeaturedIndex =
          (this.currentFeaturedIndex + 1) % this.featuredEvents.length;
      }, 6000);
    }
  }

  loadComingSoonEvents(): void {
    this.apiService.getComingSoonEvents(0, 4).subscribe({
      next: (res: HttpResponse<any>) => {
        this.comingSoonEvents = res.body?.content ?? [];
      },
    });
  }

  loadFavouriteOrganisers(): void {
    if (!this.userEmail) return;
    this.apiService.getMyFavouriteOrganisers(this.userEmail, 5).subscribe({
      next: (res: HttpResponse<FavouriteOrganiser[]>) => {
        this.favouriteOrganisers = res.body ?? [];
      },
    });
  }

  formatCurrency(value: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(value);
  }
}
